import { GatewaySettings } from '../types'

const STORAGE_KEY = 'equabot.web.settings.v1'

const DEFAULTS: GatewaySettings = {
  gatewayUrl: 'ws://localhost:18789',
  token: '',
  password: '',
}

function normalizeGatewayUrl(value: string | undefined): string {
  const trimmed = value?.trim()
  if (!trimmed) return DEFAULTS.gatewayUrl
  if (trimmed.startsWith('http://')) return trimmed.replace(/^http:\/\//, 'ws://')
  if (trimmed.startsWith('https://')) return trimmed.replace(/^https:\/\//, 'wss://')
  return trimmed
}

/**
 * Extract gateway token from URL query params if present.
 * Supports the standard equabot tokenized-URL patterns:
 *   `?equabotToken=<token>`, `?gatewayToken=<token>`, `?token=<token>`,
 * and the same names in the hash fragment.
 */
function getTokenFromUrl(): string | null {
  try {
    const names = ['equabotToken', 'gatewayToken', 'token']
    const params = new URLSearchParams(window.location.search)
    for (const name of names) {
      const value = params.get(name)?.trim()
      if (value) return value
    }

    const hash = window.location.hash?.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash
    const hashParams = new URLSearchParams(hash || '')
    for (const name of names) {
      const value = hashParams.get(name)?.trim()
      if (value) return value
    }
  } catch {
    return null
  }
  return null
}

function stripTokenFromUrl(): void {
  try {
    if (!window.history?.replaceState) return
    const names = ['equabotToken', 'gatewayToken', 'token']
    const url = new URL(window.location.href)
    let changed = false
    let hashChanged = false

    for (const name of names) {
      if (url.searchParams.has(name)) {
        url.searchParams.delete(name)
        changed = true
      }
    }

    if (url.hash) {
      const hashParams = new URLSearchParams(url.hash.slice(1))
      for (const name of names) {
        if (hashParams.has(name)) {
          hashParams.delete(name)
          changed = true
          hashChanged = true
        }
      }
      if (hashChanged) {
        const nextHash = hashParams.toString()
        url.hash = nextHash ? `#${nextHash}` : ''
      }
    }

    if (changed) {
      window.history.replaceState(window.history.state, document.title, url.toString())
    }
  } catch {
    // Best-effort cleanup only. Token persistence is handled separately.
  }
}

/**
 * Load Equabot connection settings from localStorage.
 *
 * If a gateway token is present in the URL (`?equabotToken=`, `?gatewayToken=`,
 * `?token=`, or hash equivalents), it takes precedence over the stored token
 * and is persisted for future sessions. This matches the standard equabot
 * tokenized-URL pattern used by `equabot dashboard --no-open`.
 */
export function loadEquabotSettings(): GatewaySettings {
  let settings: GatewaySettings
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      settings = { ...DEFAULTS }
    } else {
      const parsed = JSON.parse(raw) as Partial<GatewaySettings>
      settings = {
        gatewayUrl: normalizeGatewayUrl(parsed.gatewayUrl),
        token: typeof parsed.token === 'string' ? parsed.token : DEFAULTS.token,
        password: typeof parsed.password === 'string' ? parsed.password : DEFAULTS.password,
      }
    }
  } catch {
    settings = { ...DEFAULTS }
  }

  // URL token takes precedence — persist it so subsequent loads use it automatically
  const urlToken = getTokenFromUrl()
  if (urlToken) {
    if (urlToken !== settings.token) {
      settings.token = urlToken
      saveEquabotSettings(settings)
    }
    stripTokenFromUrl()
  }

  return settings
}

/**
 * Persist Equabot connection settings to localStorage.
 * Returns `true` on success, `false` if storage is unavailable (e.g. private browsing).
 */
export function saveEquabotSettings(settings: GatewaySettings): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    return true
  } catch (err) {
    console.warn('[equabot] Failed to persist settings:', err instanceof Error ? err.message : String(err))
    return false
  }
}
